import { GENDER_LABEL, LEARNER_PLAN_STATUS, LEARNER_PLAN_STATUS_LABEL, TLearnerPlanStatus } from "@/types/enum";
import { TPrepareBulkImportRow, TVisaCompanyInfo } from "@/api/services/main/learner/dtos/prepare-bulk-import.dto";
import { getNextOrCurrentMonday } from "@/features/learner/utils/calculates";

// 1回の取り込みで扱える外国人の上限。irohana visa 一括同期 (選択件数) / ファイル取り込み (データ行数) で共通。
export const MAX_IMPORT_LEARNERS = 50;

// irohana visa から取り込んだ / ファイルから取り込んだ 外国人の一括登録UI。
// 取り込み元の値 + study側で編集する項目を1行にまとめる。
// 受験分野(industryId)・受験予定(examId)は実APIのマスタ（所属機関の分野 / 分野別試験一覧）と連携する。
export type TBulkImportRow = {
  // 画面上の一意キー。visa 同期は visaEmployeeId、ファイル取り込みはファイルの行番号を使う。
  id: number;
  // irohana visa 側の従業員ID。ファイル取り込みでは持たないため null (保存時も送らない)。
  visaEmployeeId?: number | null;
  fullName: string;
  // 呼び名。ファイル取り込みのみ値が入る (visa は取得しない)。
  nickName?: string | null;
  // 管理番号。ファイル取り込みの入力値を保持するのみ (learner に保存する項目が無いため送信しない)。
  managementNumber?: string | null;
  // 前回試験の受験日 (YYYY/MM/DD)。ファイル取り込みのみ値が入り、保存時に YYYY-MM-DD へ変換して送信する。
  lastExamDate?: string | null;
  // 所属機関名 (irohana visa から取得した CompanyInRSO 名)。表示専用テキスト (spec No.7)
  companyName: string;
  // RSO内に同名 CompanyInRSO が無く、保存時に新規作成する想定 (true なら「(新規)」表示)
  isNewCompany: boolean;
  // 保存時に使う study マスタの解決済みID。突合不可は null (画面で選択させる/新規作成)
  nationalityId?: number | null;
  // 受験分野。既存所属機関は会社の分野、新規所属機関は全分野(global)から選択する。
  industryId?: number | null;
  // 受験分野(industryId)の表示名。完了画面/CSV出力で使う (突合済み分野名 or 選択した分野名)。
  industryName?: string | null;
  rsoCompanyId?: number | null;
  genderValue?: number | null;
  // 1号の起算日 (visa: skillOneStartDate を YYYY-MM-DD に変換して prepare から取得)。visa 未提供時は空。
  clause1StartDate: string;
  // ブランク期間（月単位）(visa: blankPeriodMonthly)。1号残期間/在留期限の算出に使用。
  blankPeriodMonths?: number | null;
  // visa の所属機関情報。新規 CompanyInRSO 作成時に保存へ引き継ぐ (isNewCompany のとき使用)。
  company?: TVisaCompanyInfo | null;
  // 編集項目
  lastScore: string;
  procedureManagement: boolean;
  learningApp: boolean;
  curriculumStartDate: string;
  // 受験予定。選択中の受験分野(industryId)の試験一覧(/learning-progress/industry/:id/exams)の試験ID。未選択は null。
  examId: number | null;
  examScheduleYm: string;
  // 取り込み元の表示専用項目
  nationality: string;
  gender: string;
  status: string;
  remainingPeriod: string;
  // 居住地: 都道府県(province) + 市区町村(municipality) を結合した表示専用テキスト。
  residence: string;
  // 保存用に visa の都道府県/市区町村を個別保持 (payload の prefecture/city へ渡す)。
  province: string | null;
  municipality: string | null;
  japaneseLevel: string;
  // 同じ visa 従業員が既に取り込み済みか (BE prepare が判定)。true なら画面で既存登録の警告を出す。
  isAlreadyRegistered: boolean;
};

// カリキュラム開始日の既定値: 今日が月曜なら今日、そうでなければ翌週月曜 (learner作成と同じ)
const defaultCurriculumStartDate = () => getNextOrCurrentMonday().format("YYYY/MM/DD");

// ステータス列の選択肢。値は LEARNER_PLAN_STATUS の値(色分けのキーに一致させる)
export const STATUS_OPTIONS = (Object.entries(LEARNER_PLAN_STATUS_LABEL) as [TLearnerPlanStatus, string][]).map(([value, label]) => ({
  value,
  label,
}));

// BE prepare の解決済み行 → study側の編集行 へ変換。
// 解決済みID(国籍/分野/所属機関/性別)を保持しつつ、表示・編集項目を初期化する。
export const buildImportRowFromPrepared = (row: TPrepareBulkImportRow): TBulkImportRow => ({
  id: row.visaEmployeeId,
  visaEmployeeId: row.visaEmployeeId,
  fullName: row.fullName,
  companyName: row.companyName ?? "",
  isNewCompany: row.isNewCompany,
  nationalityId: row.nationalityId,
  // 受験分野は BE が specificIndustryName から突合した industryId を初期値にする (未一致は null → 画面で選択)。
  // BE の industryId は bigint 由来で文字列になり得る。手動選択(Number変換)と型を揃えないと
  // 一括変更の同一分野チェック(===)が突合済み行と手動選択行で不一致になるため、number に正規化する。
  industryId: row.industryId != null ? Number(row.industryId) : null,
  industryName: row.industryName,
  rsoCompanyId: row.rsoCompanyId,
  genderValue: row.genderValue,
  // 1号の起算日 / ブランク期間は visa(prepare)から取得済みの値をそのまま使う。
  clause1StartDate: row.clause1StartDate ?? "",
  blankPeriodMonths: row.blankPeriodMonths ?? null,
  company: row.company,
  lastScore: "",
  procedureManagement: true,
  learningApp: true,
  curriculumStartDate: defaultCurriculumStartDate(),
  // 受験予定は分野(industryId)の試験一覧から画面で選択させる (初期は未選択)。
  examId: null,
  examScheduleYm: "",
  nationality: row.nationalityName ?? "",
  gender: row.genderValue ? (GENDER_LABEL[row.genderValue as keyof typeof GENDER_LABEL] ?? "") : "",
  status: LEARNER_PLAN_STATUS.PREPARING,
  remainingPeriod: row.specificSkillOnePeriod ?? "",
  // 居住地は 都道府県 + 市区町村 を結合して表示 (どちらか欠けても残りを表示)。
  residence: `${row.province ?? ""}${row.municipality ?? ""}`,
  province: row.province ?? null,
  municipality: row.municipality ?? null,
  // 日本語能力は visa から取得しない (prepare 応答に含まれない)。画面で選択させる。
  japaneseLevel: "",
  isAlreadyRegistered: row.isAlreadyRegistered,
});

// アカウント発行完了行 (発行後の表示専用データ)
export type TBulkAccountRow = {
  id: number;
  fullName: string;
  companyName: string;
  // 受験分野の表示名 (industryName)
  examFieldLabel: string;
  nationality: string;
  gender: string;
  loginUrl: string;
  learnerId: string;
  password: string;
};

// 取り込み編集行 + アカウント発行APIの結果 → 発行完了行 へ変換。ログイン情報は実データを使う。
export const buildAccountRowFromIssue = (
  row: TBulkImportRow,
  issue: { learnerId: number; redirectUrl: string; password: string },
): TBulkAccountRow => {
  // redirectUrl は `${origin}/login?learnerId=<表示用ID>` 形式。
  // ログインURLは learnerId 付きの完全な形でそのまま表示し、表示用IDも別途取り出す。
  const loginUrl = issue.redirectUrl;
  let learnerCode = String(issue.learnerId);
  try {
    const url = new URL(issue.redirectUrl);
    learnerCode = url.searchParams.get("learnerId") ?? learnerCode;
  } catch {
    // redirectUrl が不正でもフォールバック値で表示する
  }
  return {
    id: row.id,
    fullName: row.fullName,
    companyName: row.companyName,
    examFieldLabel: row.industryName ?? "",
    nationality: row.nationality,
    gender: row.gender,
    loginUrl,
    learnerId: learnerCode,
    password: issue.password,
  };
};

// 取り込み編集行 → 発行完了行 へ変換。ログイン情報はAPI連携前のダミー生成
export const buildAccountRowFromImport = (row: TBulkImportRow): TBulkAccountRow => {
  const learnerCode = `C0123456789-${row.id.toString().padStart(4, "0")}`;
  return {
    id: row.id,
    fullName: row.fullName,
    companyName: row.companyName,
    examFieldLabel: row.industryName ?? "",
    nationality: row.nationality,
    gender: row.gender,
    // ログインURLは learnerId 付きの完全な形で表示する
    loginUrl: `https://study.irohana.jp/login?learnerId=${learnerCode}`,
    learnerId: learnerCode,
    password: "AbCdE0123",
  };
};
